import { Component, ViewEncapsulation, Inject } from '@angular/core';
import { AppMaterialModule } from 'app/app-material.module';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { specialHoursEntry } from '../../partner-mart-create-store-multistep.component';

// Interface for time range
interface TimeRange {
  hour: number;
  minute: number;
  period: 'AM' | 'PM';
  endHour: number;
  endMinute: number;
  endPeriod: 'AM' | 'PM';
  start: Date;
  end: Date;
  startTime: string;
  endTime: string;
}

@Component({
  selector: 'add-special-hours-dialog',
  standalone: true,
  imports: [AppMaterialModule, ReactiveFormsModule, CommonModule],
  templateUrl: './add-special-hours-dialog.component.html',
  styleUrls: [
    './add-special-hours-dialog.component.scss',
    '../../../../../../../styles/m3-defaul-material-theme.scss',
  ],
  encapsulation: ViewEncapsulation.None
})
export class AddSpecialHoursDialogComponent {
  // Form Group
  specialHoursForm: FormGroup;

  specialHoursData = {
    description: '',
    startDate: null as Date | null,
    endDate: null as Date | null,
    availability: true,
    open24hrs: false
  };

  timeRanges: TimeRange[] = [
    {
      hour: 9,
      minute: 0,
      period: 'AM',
      endHour: 5,
      endMinute: 0,
      endPeriod: 'PM',
      start: (() => {
        const date = new Date();
        date.setHours(9, 0, 0, 0);
        return date;
      })(),
      end: (() => {
        const date = new Date();
        date.setHours(17, 0, 0, 0);
        return date;
      })(),
      startTime: '09:00 AM',
      endTime: '05:00 PM',
    },
  ];

  // Form Controls for time ranges
  timeRangeControls: FormControl[] = [
    new FormControl('09:00 AM', [Validators.required])
  ];

  timeOptions: { value: string; display: string }[] = [
    { value: '12:00 AM', display: '12:00 AM' },
    { value: '12:30 AM', display: '12:30 AM' },
    { value: '01:00 AM', display: '01:00 AM' },
    { value: '01:30 AM', display: '01:30 AM' },
    { value: '02:00 AM', display: '02:00 AM' },
    { value: '02:30 AM', display: '02:30 AM' },
    { value: '03:00 AM', display: '03:00 AM' },
    { value: '03:30 AM', display: '03:30 AM' },
    { value: '04:00 AM', display: '04:00 AM' },
    { value: '04:30 AM', display: '04:30 AM' },
    { value: '05:00 AM', display: '05:00 AM' },
    { value: '05:30 AM', display: '05:30 AM' },
    { value: '06:00 AM', display: '06:00 AM' },
    { value: '06:30 AM', display: '06:30 AM' },
    { value: '07:00 AM', display: '07:00 AM' },
    { value: '07:30 AM', display: '07:30 AM' },
    { value: '08:00 AM', display: '08:00 AM' },
    { value: '08:30 AM', display: '08:30 AM' },
    { value: '09:00 AM', display: '09:00 AM' },
    { value: '09:30 AM', display: '09:30 AM' },
    { value: '10:00 AM', display: '10:00 AM' },
    { value: '10:30 AM', display: '10:30 AM' },
    { value: '11:00 AM', display: '11:00 AM' },
    { value: '11:30 AM', display: '11:30 AM' },
    { value: '12:00 PM', display: '12:00 PM' },
    { value: '12:30 PM', display: '12:30 PM' },
    { value: '01:00 PM', display: '01:00 PM' },
    { value: '01:30 PM', display: '01:30 PM' },
    { value: '02:00 PM', display: '02:00 PM' },
    { value: '02:30 PM', display: '02:30 PM' },
    { value: '03:00 PM', display: '03:00 PM' },
    { value: '03:30 PM', display: '03:30 PM' },
    { value: '04:00 PM', display: '04:00 PM' },
    { value: '04:30 PM', display: '04:30 PM' },
    { value: '05:00 PM', display: '05:00 PM' },
    { value: '05:30 PM', display: '05:30 PM' },
    { value: '06:00 PM', display: '06:00 PM' },
    { value: '06:30 PM', display: '06:30 PM' },
    { value: '07:00 PM', display: '07:00 PM' },
    { value: '08:00 PM', display: '08:00 PM' },
    { value: '09:00 PM', display: '09:00 PM' },
    { value: '10:00 PM', display: '10:00 PM' },
    { value: '11:00 PM', display: '11:00 PM' },
    { value: '11:30 PM', display: '11:30 PM' },
    { value: '11:59 PM', display: '11:59 PM' },
  ];

  // Store existing special hours date ranges
  existingSpecialHours: { startDate: Date; endDate: Date }[] = [];

  constructor(
    private dialogRef: MatDialogRef<AddSpecialHoursDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    // Initialize existing special hours if provided
    if (this.data?.existingSpecialHours) {
      this.existingSpecialHours = this.data.existingSpecialHours;
    }
    this.specialHoursForm = new FormGroup({
      description: new FormControl('', [Validators.required]),
      startDate: new FormControl<Date | null>(null, [Validators.required]),
      endDate: new FormControl<Date | null>(null, [Validators.required]),
      availability: new FormControl(true),
      open24hrs: new FormControl(false)
    });

    // Listen to start date changes to update end date minimum
    this.startDateControl.valueChanges.subscribe((startDate: Date | null) => {
      if (!startDate) {
        // Clear end date if start date is cleared
        this.endDateControl.setValue(null);
      } else if (this.endDateControl.value && this.endDateControl.value < startDate) {
        // Reset end date if it's before the new start date
        this.endDateControl.setValue(null);
      }
    });

    // Listen to availability changes to disable/enable 24hrs
    this.availabilityControl.valueChanges.subscribe((isAvailable: boolean) => {
      // If store is set to closed, disable and uncheck 24hrs
      if (!isAvailable) {
        this.open24hrsControl.setValue(false);
      }
    });

    // Listen to 24hrs checkbox changes to set time ranges
    this.open24hrsControl.valueChanges.subscribe((is24hrs: boolean) => {
      if (is24hrs) {
        // Set all time ranges to 24 hours (12:00 AM to 11:59 PM)
        this.timeRanges = this.timeRanges.map(range => ({
          ...range,
          hour: 12,
          minute: 0,
          period: 'AM' as 'AM' | 'PM',
          endHour: 11,
          endMinute: 59,
          endPeriod: 'PM' as 'AM' | 'PM',
          start: (() => {
            const date = new Date();
            date.setHours(0, 0, 0, 0);
            return date;
          })(),
          end: (() => {
            const date = new Date();
            date.setHours(23, 59, 0, 0);
            return date;
          })(),
          startTime: '12:00 AM',
          endTime: '11:59 PM'
        }));
      }
    });
  }

  addTimeRange() {
    // Find the next available time slot
    const availableSlot = this.findNextAvailableTimeSlot();

    const newRange = {
      hour: availableSlot.startHour,
      minute: availableSlot.startMinute,
      period: availableSlot.startPeriod as 'AM' | 'PM',
      endHour: availableSlot.endHour,
      endMinute: availableSlot.endMinute,
      endPeriod: availableSlot.endPeriod as 'AM' | 'PM',
      start: availableSlot.startDate,
      end: availableSlot.endDate,
      startTime: availableSlot.startTime,
      endTime: availableSlot.endTime,
    };

    this.timeRanges = [...this.timeRanges, newRange];
  }

  // Find the next available time slot that doesn't overlap with existing ranges
  private findNextAvailableTimeSlot(): {
    startHour: number;
    startMinute: number;
    startPeriod: string;
    endHour: number;
    endMinute: number;
    endPeriod: string;
    startDate: Date;
    endDate: Date;
    startTime: string;
    endTime: string;
  } {
    // If no existing time ranges, use default 9AM-5PM
    if (this.timeRanges.length === 0) {
      const defaultStartDate = new Date();
      defaultStartDate.setHours(9, 0, 0, 0);
      const defaultEndDate = new Date();
      defaultEndDate.setHours(17, 0, 0, 0);

      return {
        startHour: 9,
        startMinute: 0,
        startPeriod: 'AM',
        endHour: 5,
        endMinute: 0,
        endPeriod: 'PM',
        startDate: defaultStartDate,
        endDate: defaultEndDate,
        startTime: '09:00 AM',
        endTime: '05:00 PM',
      };
    }

    // Find gaps between existing time ranges
    const sortedRanges = [...this.timeRanges]
      .filter(range => range.startTime && range.endTime)
      .sort((a, b) => this.timeToMinutes(a.startTime) - this.timeToMinutes(b.startTime));

    // Check for gap at the beginning of the day (12:00 AM to first range)
    if (sortedRanges.length > 0) {
      const firstRange = sortedRanges[0];
      const firstStartMinutes = this.timeToMinutes(firstRange.startTime);

      if (firstStartMinutes > 60) { // If first range starts after 1:00 AM
        const earlyStartDate = new Date();
        earlyStartDate.setHours(0, 0, 0, 0);
        const earlyEndDate = new Date();
        earlyEndDate.setHours(0, 30, 0, 0);

        return {
          startHour: 12,
          startMinute: 0,
          startPeriod: 'AM',
          endHour: 12,
          endMinute: 30,
          endPeriod: 'AM',
          startDate: earlyStartDate,
          endDate: earlyEndDate,
          startTime: '12:00 AM',
          endTime: '12:30 AM',
        };
      }
    }

    // Check for gaps between existing ranges
    for (let i = 0; i < sortedRanges.length - 1; i++) {
      const currentRange = sortedRanges[i];
      const nextRange = sortedRanges[i + 1];

      const currentEndMinutes = this.timeToMinutes(currentRange.endTime);
      const nextStartMinutes = this.timeToMinutes(nextRange.startTime);

      // If there's at least 1 hour gap between ranges
      if (nextStartMinutes - currentEndMinutes >= 60) {
        const gapStartMinutes = currentEndMinutes + 30; // Start 30 minutes after current range ends
        const gapEndMinutes = Math.min(gapStartMinutes + 60, nextStartMinutes - 30); // End 30 minutes before next range starts

        const startTime = this.minutesToTimeString(gapStartMinutes);
        const endTime = this.minutesToTimeString(gapEndMinutes);

        const gapStartDate = new Date();
        gapStartDate.setHours(Math.floor(gapStartMinutes / 60), gapStartMinutes % 60, 0, 0);
        const gapEndDate = new Date();
        gapEndDate.setHours(Math.floor(gapEndMinutes / 60), gapEndMinutes % 60, 0, 0);

        return {
          startHour: this.get12Hour(gapStartDate),
          startMinute: gapStartDate.getMinutes(),
          startPeriod: gapStartDate.getHours() < 12 ? 'AM' : 'PM',
          endHour: this.get12Hour(gapEndDate),
          endMinute: gapEndDate.getMinutes(),
          endPeriod: gapEndDate.getHours() < 12 ? 'AM' : 'PM',
          startDate: gapStartDate,
          endDate: gapEndDate,
          startTime,
          endTime,
        };
      }
    }

    // Check for gap at the end of the day (after last range)
    if (sortedRanges.length > 0) {
      const lastRange = sortedRanges[sortedRanges.length - 1];
      const lastEndMinutes = this.timeToMinutes(lastRange.endTime);

      if (lastEndMinutes < 1380) { // If last range ends before 11:00 PM
        const gapStartMinutes = lastEndMinutes + 30;
        const gapEndMinutes = Math.min(gapStartMinutes + 60, 1380); // End at 11:00 PM max

        const startTime = this.minutesToTimeString(gapStartMinutes);
        const endTime = this.minutesToTimeString(gapEndMinutes);

        const lastGapStartDate = new Date();
        lastGapStartDate.setHours(Math.floor(gapStartMinutes / 60), gapStartMinutes % 60, 0, 0);
        const lastGapEndDate = new Date();
        lastGapEndDate.setHours(Math.floor(gapEndMinutes / 60), gapEndMinutes % 60, 0, 0);

        return {
          startHour: this.get12Hour(lastGapStartDate),
          startMinute: lastGapStartDate.getMinutes(),
          startPeriod: lastGapStartDate.getHours() < 12 ? 'AM' : 'PM',
          endHour: this.get12Hour(lastGapEndDate),
          endMinute: lastGapEndDate.getMinutes(),
          endPeriod: lastGapEndDate.getHours() < 12 ? 'AM' : 'PM',
          startDate: lastGapStartDate,
          endDate: lastGapEndDate,
          startTime,
          endTime,
        };
      }
    }

    // If no gaps found, try to find a slot after 6:00 PM
    const eveningStartMinutes = 18 * 60; // 6:00 PM
    const eveningEndMinutes = 20 * 60; // 8:00 PM

    // Check if 6:00 PM - 8:00 PM is available
    const isEveningSlotAvailable = !sortedRanges.some(range => {
      const rangeStart = this.timeToMinutes(range.startTime);
      const rangeEnd = this.timeToMinutes(range.endTime);
      return (eveningStartMinutes < rangeEnd && eveningEndMinutes > rangeStart);
    });

    if (isEveningSlotAvailable) {
      const eveningStartDate = new Date();
      eveningStartDate.setHours(18, 0, 0, 0);
      const eveningEndDate = new Date();
      eveningEndDate.setHours(20, 0, 0, 0);

      return {
        startHour: 6,
        startMinute: 0,
        startPeriod: 'PM',
        endHour: 8,
        endMinute: 0,
        endPeriod: 'PM',
        startDate: eveningStartDate,
        endDate: eveningEndDate,
        startTime: '06:00 PM',
        endTime: '08:00 PM',
      };
    }

    // Fallback: use 10:00 PM - 11:30 PM
    const fallbackStartDate = new Date();
    fallbackStartDate.setHours(22, 0, 0, 0);
    const fallbackEndDate = new Date();
    fallbackEndDate.setHours(23, 30, 0, 0);

    return {
      startHour: 10,
      startMinute: 0,
      startPeriod: 'PM',
      endHour: 11,
      endMinute: 30,
      endPeriod: 'PM',
      startDate: fallbackStartDate,
      endDate: fallbackEndDate,
      startTime: '10:00 PM',
      endTime: '11:30 PM',
    };
  }

  // Convert minutes back to time string
  private minutesToTimeString(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    let displayHour = hours;
    let period = 'AM';

    if (hours === 0) {
      displayHour = 12;
    } else if (hours > 12) {
      displayHour = hours - 12;
      period = 'PM';
    } else if (hours === 12) {
      period = 'PM';
    }

    const hourStr = displayHour.toString().padStart(2, '0');
    const minStr = mins.toString().padStart(2, '0');

    return `${hourStr}:${minStr} ${period}`;
  }

  removeTimeRange(index: number) {
    // Remove the time range at the specified index
    this.timeRanges.splice(index, 1);
  }

  onStartTimeChange(index: number, newValue: string) {
    const timeRange = this.timeRanges[index];
    timeRange.startTime = newValue;

    if (timeRange.startTime) {
      // Parse the time string (e.g., "09:00 AM")
      const timeMatch = timeRange.startTime.match(/(\d+):(\d+)\s*(AM|PM)/);
      if (timeMatch) {
        let hour = parseInt(timeMatch[1], 10);
        const minute = parseInt(timeMatch[2], 10);
        const period = timeMatch[3] as 'AM' | 'PM';

        // Convert 12-hour to 24-hour for Date object
        if (period === 'PM' && hour !== 12) {
          hour += 12;
        }
        if (period === 'AM' && hour === 12) {
          hour = 0;
        }

        // Create a new date based on the current timeRange.start to preserve any existing date
        const date = new Date(timeRange.start);
        date.setHours(hour, minute, 0, 0);

        timeRange.hour = this.get12Hour(date);
        timeRange.minute = date.getMinutes();
        timeRange.period = period;
        timeRange.start = date;

        // Check if current end time is now invalid and reset it if necessary
        if (timeRange.endTime && this.isEndTimeInvalid(index)) {
          timeRange.endTime = '';
          timeRange.end = new Date();
        }
      }
    }
  }

  onEndTimeChange(index: number, newValue: string) {
    const timeRange = this.timeRanges[index];
    timeRange.endTime = newValue;

    if (timeRange.endTime) {
      // Parse the time string (e.g., "05:00 PM")
      const timeMatch = timeRange.endTime.match(/(\d+):(\d+)\s*(AM|PM)/);
      if (timeMatch) {
        let hour = parseInt(timeMatch[1], 10);
        const minute = parseInt(timeMatch[2], 10);
        const period = timeMatch[3] as 'AM' | 'PM';

        // Convert 12-hour to 24-hour for Date object
        if (period === 'PM' && hour !== 12) {
          hour += 12;
        }
        if (period === 'AM' && hour === 12) {
          hour = 0;
        }

        // Create a new date based on the current timeRange.end to preserve any existing date
        const date = new Date(timeRange.end);
        date.setHours(hour, minute, 0, 0);

        timeRange.endHour = this.get12Hour(date);
        timeRange.endMinute = date.getMinutes();
        timeRange.endPeriod = period;
        timeRange.end = date;
      }
    }
  }

  // Getter methods for form controls
  get descriptionControl() {
    return this.specialHoursForm.get('description') as FormControl;
  }

  get startDateControl() {
    return this.specialHoursForm.get('startDate') as FormControl;
  }

  get endDateControl() {
    return this.specialHoursForm.get('endDate') as FormControl;
  }

  get availabilityControl() {
    return this.specialHoursForm.get('availability') as FormControl;
  }

  get open24hrsControl() {
    return this.specialHoursForm.get('open24hrs') as FormControl;
  }

  private get12Hour(date: Date): number {
    const hour = date.getHours();
    return hour % 12 === 0 ? 12 : hour % 12;
  }

  // Convert time string to minutes for comparison
  private timeToMinutes(timeString: string): number {
    const timeMatch = timeString.match(/(\d+):(\d+)\s*(AM|PM)/);
    if (!timeMatch) {return 0;}

    let hour = parseInt(timeMatch[1], 10);
    const minute = parseInt(timeMatch[2], 10);
    const period = timeMatch[3] as 'AM' | 'PM';

    // Convert 12-hour to 24-hour
    if (period === 'PM' && hour !== 12) {
      hour += 12;
    }
    if (period === 'AM' && hour === 12) {
      hour = 0;
    }

    return hour * 60 + minute;
  }

  // Check if end time is invalid (less than or equal to start time)
  isEndTimeInvalid(index: number): boolean {
    const timeRange = this.timeRanges[index];
    if (!timeRange.startTime || !timeRange.endTime) {return false;}

    const startMinutes = this.timeToMinutes(timeRange.startTime);
    const endMinutes = this.timeToMinutes(timeRange.endTime);

    return endMinutes <= startMinutes;
  }

  // Check if two time ranges overlap
  private timeRangesOverlap(range1: TimeRange, range2: TimeRange): boolean {
    if (!range1.startTime || !range1.endTime || !range2.startTime || !range2.endTime) {
      return false;
    }

    const start1 = this.timeToMinutes(range1.startTime);
    const end1 = this.timeToMinutes(range1.endTime);
    const start2 = this.timeToMinutes(range2.startTime);
    const end2 = this.timeToMinutes(range2.endTime);

    // Check if ranges overlap: start1 < end2 && start2 < end1
    return start1 < end2 && start2 < end1;
  }

  // Check if there are overlapping time ranges within the current component
  hasInternalTimeOverlaps(): boolean {
    for (let i = 0; i < this.timeRanges.length; i++) {
      for (let j = i + 1; j < this.timeRanges.length; j++) {
        const range1 = this.timeRanges[i];
        const range2 = this.timeRanges[j];

        if (range1.startTime && range1.endTime && range2.startTime && range2.endTime) {
          if (this.timeRangesOverlap(range1, range2)) {
            return true;
          }
        }
      }
    }
    return false;
  }

  // Get filtered end time options based on start time and prevent overlaps
  getEndTimeOptions(index: number): { value: string; display: string }[] {
    const timeRange = this.timeRanges[index];
    if (!timeRange.startTime) {
      return this.timeOptions;
    }

    const startMinutes = this.timeToMinutes(timeRange.startTime);

    return this.timeOptions.filter(option => {
      const optionMinutes = this.timeToMinutes(option.value);

      // Must be after the start time
      if (optionMinutes <= startMinutes) {
        return false;
      }

      // Check if this end time would overlap with any other time range
      for (let i = 0; i < this.timeRanges.length; i++) {
        if (i === index) {continue;} // Skip current time range

        const otherRange = this.timeRanges[i];
        if (otherRange.startTime && otherRange.endTime) {
          const otherStartMinutes = this.timeToMinutes(otherRange.startTime);
          const otherEndMinutes = this.timeToMinutes(otherRange.endTime);

          // Check if the option time falls within another time range
          // or if the new range (startTime to option) would overlap with other range
          if ((optionMinutes > otherStartMinutes && optionMinutes <= otherEndMinutes) ||
              (startMinutes < otherEndMinutes && optionMinutes > otherStartMinutes)) {
            return false;
          }
        }
      }

      return true;
    });
  }

  // Get filtered start time options that don't overlap with existing time ranges
  getStartTimeOptions(currentIndex: number): { value: string; display: string }[] {
    return this.timeOptions.filter(option => {
      const optionMinutes = this.timeToMinutes(option.value);

      // Check if this start time would overlap with any other time range
      for (let i = 0; i < this.timeRanges.length; i++) {
        if (i === currentIndex) {continue;} // Skip current time range

        const otherRange = this.timeRanges[i];
        if (otherRange.startTime && otherRange.endTime) {
          const otherStartMinutes = this.timeToMinutes(otherRange.startTime);
          const otherEndMinutes = this.timeToMinutes(otherRange.endTime);

          // Check if the option time falls within another time range
          if (optionMinutes >= otherStartMinutes && optionMinutes < otherEndMinutes) {
            return false;
          }
        }
      }

      return true;
    });
  }

  // Date filter to prevent selecting dates that overlap with existing special hours and dates before current date
  startDateFilter = (date: Date | null): boolean => {
    if (!date) {
      return true;
    }

    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);

    // Check if date is before current date
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    if (compareDate < currentDate) {
      return false;
    }

    // Check if this date falls within any existing special hours range
    return !this.existingSpecialHours.some(existing => {
      const existingStart = new Date(existing.startDate);
      const existingEnd = new Date(existing.endDate);
      existingStart.setHours(0, 0, 0, 0);
      existingEnd.setHours(0, 0, 0, 0);

      return compareDate >= existingStart && compareDate <= existingEnd;
    });
  };

  // Date filter to prevent selecting end dates before start date, before current date, and overlapping with existing special hours
  endDateFilter = (date: Date | null): boolean => {
    if (!date) {
      return true;
    }

    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);

    // Check if date is before current date
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    if (compareDate < currentDate) {
      return false;
    }

    // First check if end date is after start date
    if (this.startDateControl.value) {
      const startDate = new Date(this.startDateControl.value);
      startDate.setHours(0, 0, 0, 0);

      if (compareDate < startDate) {
        return false; // End date cannot be before start date
      }
    }

    // Then check if this date falls within any existing special hours range
    return !this.existingSpecialHours.some(existing => {
      const existingStart = new Date(existing.startDate);
      const existingEnd = new Date(existing.endDate);
      existingStart.setHours(0, 0, 0, 0);
      existingEnd.setHours(0, 0, 0, 0);

      return compareDate >= existingStart && compareDate <= existingEnd;
    });
  };

  // Check if the selected date range overlaps with any existing special hours
  hasDateOverlap(): boolean {
    const startDate = this.startDateControl.value;
    const endDate = this.endDateControl.value;

    if (!startDate || !endDate) {
      return false;
    }

    const newStart = new Date(startDate);
    const newEnd = new Date(endDate);
    newStart.setHours(0, 0, 0, 0);
    newEnd.setHours(23, 59, 59, 999); // Include the entire end date

    return this.existingSpecialHours.some(existing => {
      const existingStart = new Date(existing.startDate);
      const existingEnd = new Date(existing.endDate);
      existingStart.setHours(0, 0, 0, 0);
      existingEnd.setHours(23, 59, 59, 999); // Include the entire end date

      // Check if the new range overlaps with the existing range
      // Two ranges overlap if: newStart <= existingEnd && newEnd >= existingStart
      return newStart <= existingEnd && newEnd >= existingStart;
    });
  }



  saveSpecialHours() {
    // Check if form is valid
    if (this.specialHoursForm.invalid) {
      return;
    }

    // Check for invalid time ranges (end time <= start time)
    if (this.hasInvalidTimeRanges()) {
      return;
    }

    // Check for internal time overlaps
    if (this.hasInternalTimeOverlaps()) {
      return;
    }

    // Check for date overlap
    if (this.hasDateOverlap()) {
      return;
    }

    const specialHoursData: specialHoursEntry = {
      description: this.specialHoursForm.get('description')?.value || '',
      startDate: this.specialHoursForm.get('startDate')?.value || new Date(),
      endDate: this.specialHoursForm.get('endDate')?.value || new Date(),
      availability: this.specialHoursForm.get('availability')?.value,
      open24hrs: this.specialHoursForm.get('open24hrs')?.value || false,
      timeRanges: this.timeRanges
    };

    this.dialogRef.close(specialHoursData);
  }

  // Check if any time range has invalid end time
  hasInvalidTimeRanges(): boolean {
    return this.timeRanges.some((_, index) => this.isEndTimeInvalid(index));
  }

  cancel() {
    this.dialogRef.close();
  }
}
